import React, { useState, useEffect } from "react";
import { rewriteChapter } from "@/lib/rewriteChapter";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RefreshCw, Coins } from "lucide-react";

const LENGTHS = [
  { v: "scene", label: "Scene", hint: "~350–500 words" },
  { v: "short", label: "Standard", hint: "~700–1000 words" },
  { v: "full", label: "Full", hint: "~1300–1700 words" },
];
const RW_INTENSITY = [
  { v: null, label: "Match book" },
  { v: 0, label: "Tame" },
  { v: 1, label: "Standard" },
  { v: 2, label: "Gritty" },
  { v: 3, label: "Unflinching" },
  { v: 4, label: "Brutal" },
];
const RW_PACING = [
  { v: null, label: "Match book" },
  { v: "slow", label: "Slow-burn" },
  { v: "standard", label: "Standard" },
  { v: "fast", label: "Fast-paced" },
];
const RW_HUMOUR = [
  { v: null, label: "Match book" },
  { v: 0, label: "None" },
  { v: 1, label: "Dry" },
  { v: 2, label: "Light" },
  { v: 3, label: "Playful" },
  { v: 4, label: "Comic" },
];
const RW_GORE = [
  { v: null, label: "Match book" },
  { v: true, label: "On" },
  { v: false, label: "Off" },
];
const RW_DREAD = [
  { v: null, label: "Match book" },
  { v: 0, label: "Off" },
  { v: 1, label: "Eerie" },
  { v: 2, label: "Unsettling" },
  { v: 3, label: "Sinister" },
  { v: 4, label: "Terrifying" },
];
const COST = 4;

export default function RewriteChapterDialog({ open, onOpenChange, projectId, chapter, onDone }) {
  const [reason, setReason] = useState("");
  const [target, setTarget] = useState("short");
  const [customWords, setCustomWords] = useState("");
  const [intensity, setIntensity] = useState(null);
  const [humour, setHumour] = useState(null);
  const [dread, setDread] = useState(null);
  const [pacing, setPacing] = useState(null);
  const [gore, setGore] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason(""); setTarget("short"); setCustomWords(""); setIntensity(null); setHumour(null); setDread(null); setPacing(null); setGore(null); setBusy(false); }
  }, [open]);

  const rewrite = async () => {
    if (!chapter?.id) return;
    setBusy(true);
    try {
      const body = { reason: reason.trim(), target };
      if (customWords && Number(customWords) > 0) body.words_per_chapter = Number(customWords);
      if (intensity !== null) body.intensity = intensity;
      if (humour !== null) body.humour = humour;
      if (dread !== null) body.dread = dread;
      if (pacing !== null) body.pacing = pacing;
      if (gore !== null) body.gore_pass = gore;
      const data = await rewriteChapter(projectId, chapter.id, body);
      toast.success("Chapter rewritten — the old version is saved to History");
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't rewrite this chapter"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="rewrite-chapter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-primary" /> Rewrite this chapter</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <p className="text-sm text-muted-foreground">
            Re-drafts <b>“{chapter?.title || "this chapter"}”</b> from scratch, keeping your Codex canon, the story so far,
            this chapter’s synopsis and beats — and it flows on from the previous chapter and into the next one. The current
            text is saved to <b>History</b> first, so you can restore it any time.
          </p>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Why does it need rewriting? <span className="text-muted-foreground/70">(optional — what to fix or change)</span></label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="rewrite-reason" className="mt-1"
              placeholder="e.g. The pacing drags in the middle, and Mara should discover the letter here — not just hear about it. Make the confrontation more tense." />
            <p className="text-[10px] text-muted-foreground mt-1">The AI treats this as the top-priority instruction for the new version.</p>
          </div>

          {Array.isArray(chapter?.rewrite_memory) && chapter.rewrite_memory.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5" data-testid="rewrite-memory">
              <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">Earlier rewrite notes it will still honour</p>
              <ul className="mt-1 space-y-0.5">
                {chapter.rewrite_memory.map((m, i) => (
                  <li key={i} data-testid={`rewrite-memory-${i}`} className="text-[11px] text-muted-foreground leading-snug">• {m.reason}</li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground/70 mt-1.5">Cleared automatically when you mark the chapter Complete.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground">Chapter length</label>
            <div className="mt-1 grid grid-cols-3 gap-1.5">
              {LENGTHS.map((l) => (
                <button key={l.v} type="button" onClick={() => { setTarget(l.v); setCustomWords(""); }} data-testid={`rewrite-length-${l.v}`}
                  className={`rounded-lg border px-2 py-2 text-center transition-colors ${target === l.v && !customWords ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                  <div className="text-xs font-medium">{l.label}</div>
                  <div className="text-[10px] text-muted-foreground">{l.hint}</div>
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">or custom</span>
              <Input type="number" min="100" step="50" value={customWords} onChange={(e) => setCustomWords(e.target.value)}
                placeholder="words for this chapter (e.g. 2000)" data-testid="rewrite-custom-words" className="h-8 text-xs" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Pacing <span className="text-muted-foreground/70">(for this rewrite)</span></label>
            <div className="mt-1 grid grid-cols-2 gap-1.5" data-testid="rewrite-pacing">
              {RW_PACING.map((p) => (
                <button key={String(p.v)} type="button" onClick={() => setPacing(p.v)} data-testid={`rewrite-pacing-${p.v === null ? "book" : p.v}`}
                  className={`rounded-lg border px-1 py-1.5 text-center text-[11px] font-medium transition-colors ${pacing === p.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Content intensity <span className="text-muted-foreground/70">(for this rewrite)</span></label>
            <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid="rewrite-intensity">
              {RW_INTENSITY.map((it) => (
                <button key={String(it.v)} type="button" onClick={() => setIntensity(it.v)} data-testid={`rewrite-intensity-${it.v === null ? "book" : it.v}`}
                  className={`rounded-lg border px-1 py-1.5 text-center text-[11px] font-medium transition-colors ${intensity === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                  {it.label}
                </button>
              ))}
            </div>
            {(intensity === 4 || intensity === null) && (
              <div className="mt-2" data-testid="rewrite-gore-pass">
                <label className="text-[11px] font-medium text-muted-foreground">Gore pass <span className="text-muted-foreground/70">(Brutal only — extra AI pass for full gruesome detail)</span></label>
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  {RW_GORE.map((g) => (
                    <button key={String(g.v)} type="button" onClick={() => setGore(g.v)} data-testid={`rewrite-gore-${g.v === null ? "book" : g.v ? "on" : "off"}`}
                      className={`rounded-lg border px-1 py-1.5 text-center text-[11px] font-medium transition-colors ${gore === g.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-1">How graphic violence/injury get in this scene. “Match book” uses the book’s setting; typing “gruesome/visceral” above also pushes it up.</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Humour &amp; wit <span className="text-muted-foreground/70">(for this rewrite)</span></label>
            <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid="rewrite-humour">
              {RW_HUMOUR.map((it) => (
                <button key={String(it.v)} type="button" onClick={() => setHumour(it.v)} data-testid={`rewrite-humour-${it.v === null ? "book" : it.v}`}
                  className={`rounded-lg border px-1 py-1.5 text-center text-[11px] font-medium transition-colors ${humour === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                  {it.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Dry wit and banter for this scene. “Match book” uses the book’s humour dial.</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Dread <span className="text-muted-foreground/70">(for this rewrite)</span></label>
            <div className="mt-1 grid grid-cols-3 gap-1.5" data-testid="rewrite-dread">
              {RW_DREAD.map((it) => (
                <button key={String(it.v)} type="button" onClick={() => setDread(it.v)} data-testid={`rewrite-dread-${it.v === null ? "book" : it.v}`}
                  className={`rounded-lg border px-1 py-1.5 text-center text-[11px] font-medium transition-colors ${dread === it.v ? "bg-primary/10 border-primary" : "bg-background border-border hover:border-primary/50"}`}>
                  {it.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">How sinister and frightening this scene feels. A horror genre or tone raises it to at least Sinister.</p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="rewrite-cost">
            <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> Rewriting one chapter</span>
            <span className="text-sm font-semibold">{COST} credits</span>
          </div>

          <Button className="w-full gap-2" onClick={rewrite} disabled={busy || !chapter?.id} data-testid="rewrite-start">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {busy ? "Rewriting…" : "Rewrite chapter"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
